import {
  AsyncSelect,
  Button,
  Checkbox,
  FlashMessage,
  Input,
  Modal,
  RichTextEditor,
} from "../../common";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Grid, InputLabel } from "@material-ui/core";
import PinkPlus from "../icons/plus-pink.svg";
import ExternalLinkIcon from "../icons/external-link.svg";
import DeleteIcon from "../icons/delete-icon.svg";
import DateIcon from "../icons/date-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import _ from "lodash";
import { updateCandidateWorkExperienceRequest } from "../../../reducers/candidate/candidate.reducer";
import { mergeSessionUserPartial } from "../../../reducers/auth/session.reducer";
import commonServices from "../../../services/common.services";
import EditIcon from "../icons/edit-icon.svg";
import JobTitleIcon from "../icons/job-title-icon.svg";
import LocationIcon from "../icons/location-select.svg";
import { stripHTMLTags } from "../../../utils/helper";
import ReadMoreLess from "../../common/ReadMoreLess";
import WorkExperienceIcon from "../icons/work-experience-icon.svg";

interface ExperienceItem {
  id: number;
  user_id: number;
  job_id: number | null;
  job_title_custom: string;
  is_present?: number | boolean;
  company_name: string;
  description: string;
  job_started: string;
  job_ended: string | null;
  job_location_id: number | null;
  is_current_job: number;
  job_site: string | null;
  jobTitle:
  | { label: string; value: number | string; __isNew__?: boolean }
  | string
  | null;
  location:
  | { label: string; value: number | string; id?: number; location?: string }
  | string
  | null;
}

interface WorkExperienceProps {
  experiences: ExperienceItem[];
  isPublicPage?: boolean;
  openRequest?: boolean;
  onOpenHandled?: () => void;
}
let searchDelay: ReturnType<typeof setTimeout>;
let locationSearchDelay: ReturnType<typeof setTimeout>;

const isWholeNumber = (value: any) => {
  if (value === null || value === undefined || value === "") return false;
  const num = Number(value);
  return !isNaN(num) && num % 1 === 0;
};

const toJobTitleOption = (raw: unknown) => {
  if (_.isPlainObject(raw)) {
    const label = String(
      _.get(raw, "label", "") || _.get(raw, "value", "")
    ).trim();
    const v = _.get(raw, "value", label);
    const isNew = Boolean(_.get(raw, "__isNew__", false));
    if (!label && (v === null || v === undefined || v === "")) return null;
    return { label: label || String(v), value: v, ...(isNew ? { __isNew__: true } : {}) };
  }
  if (typeof raw === "string" && raw.trim()) {
    const t = raw.trim();
    return { label: t, value: t, __isNew__: true };
  }
  return null;
};

const buildJobTitleOptionForForm = (exp: ExperienceItem) => {
  const fromJobTitle = toJobTitleOption(exp?.jobTitle);
  if (fromJobTitle) return fromJobTitle;

  const hasPresetId =
    exp?.job_id != null && isWholeNumber(Number(exp.job_id));
  if (hasPresetId) {
    return {
      label: String(_.get(exp, "jobTitle.label", "") || "").trim() || `Job #${exp.job_id}`,
      value: Number(exp.job_id),
    };
  }

  const custom = String(exp?.job_title_custom || "").trim();
  if (custom) {
    return { label: custom, value: custom, __isNew__: true };
  }

  return "";
};

const toLocationOption = (raw: unknown) => {
  if (_.isPlainObject(raw)) {
    const value = _.get(raw, "value", _.get(raw, "id", null));
    if (value === null || value === undefined || value === "") return null;
    const label = String(
      _.get(raw, "label", "") || _.get(raw, "location", "") || value
    ).trim();
    const numericId = Number(value);
    return {
      label: label || String(value),
      value: isWholeNumber(numericId) ? numericId : value,
    };
  }
  return null;
};

const buildLocationOptionForForm = (exp: ExperienceItem) => {
  const fromLocation = toLocationOption(exp?.location);
  if (fromLocation) return fromLocation;

  if (exp?.job_location_id != null && exp.job_location_id !== ("" as any)) {
    const numericId = Number(exp.job_location_id);
    if (isWholeNumber(numericId)) {
      return {
        label: `Location #${numericId}`,
        value: numericId,
      };
    }
  }

  return null;
};

const normalizeExperienceRowForForm = (exp: ExperienceItem): ExperienceItem => ({
  ...exp,
  jobTitle: buildJobTitleOptionForForm(exp),
  location: buildLocationOptionForForm(exp),
});

/** After save, align persisted columns with the selected option so list UI updates immediately */
const syncPersistedTitleFields = (row: any) => {
  const opt = toJobTitleOption(row?.jobTitle);
  if (!opt) return row;

  const isCustom = Boolean((opt as any).__isNew__);
  const numericId =
    !isCustom && isWholeNumber(Number(opt.value)) ? Number(opt.value) : null;

  if (numericId != null) {
    return {
      ...row,
      job_id: numericId,
      job_title_custom: "",
      jobTitle: { label: opt.label, value: numericId },
    };
  }

  const label = String(opt.label || opt.value || "").trim();
  return {
    ...row,
    job_id: null,
    job_title_custom: label,
    jobTitle: label ? { label, value: label, __isNew__: true } : "",
  };
};

const syncPersistedLocationFields = (row: any) => {
  const opt = toLocationOption(row?.location);
  if (!opt) {
    return {
      ...row,
      job_location_id: null,
      location: null,
    };
  }

  const numericId = Number(opt.value);
  const locationId = isWholeNumber(numericId) ? numericId : opt.value;
  return {
    ...row,
    job_location_id: locationId,
    location: {
      id: locationId,
      location: opt.label,
      value: locationId,
      label: opt.label,
    },
  };
};

/** Shape stored on session user / API row (no react-select value stuck in job_title_custom) */
const toSessionExperienceRow = (row: any) => {
  const base = syncPersistedLocationFields(syncPersistedTitleFields(row));
  const jobId = base?.job_id != null ? Number(base.job_id) : null;
  const hasJob = jobId != null && isWholeNumber(jobId);
  const locationId =
    base?.job_location_id != null ? Number(base.job_location_id) : null;
  const hasLocation = locationId != null && isWholeNumber(locationId);
  const locationLabel = String(
    _.get(base, "location.label", "") || _.get(base, "location.location", "")
  ).trim();
  return {
    id: base.id,
    user_id: base.user_id,
    job_id: hasJob ? jobId : null,
    job_title_custom: hasJob ? "" : String(base.job_title_custom || "").trim(),
    is_current_job: base.is_current_job,
    company_name: base.company_name,
    description: base.description,
    job_started: base.job_started,
    job_ended: base.job_ended,
    job_location_id: hasLocation ? locationId : null,
    is_present: base.is_present,
    job_site: base.job_site,
    jobTitle: hasJob ? base.jobTitle ?? null : null,
    location: hasLocation
      ? { id: locationId, location: locationLabel }
      : null,
  };
};

const normalizeExperienceRowForApi = (exp: any) => {
  const next = { ...exp };
  const opt =
    toJobTitleOption(next?.jobTitle) || toJobTitleOption(next?.job_title_custom);
  if (opt) {
    next.jobTitle = opt;
  }
  if (_.isPlainObject(next.job_title_custom)) {
    next.job_title_custom =
      String(
        _.get(next.job_title_custom, "label", "") ||
        _.get(next.job_title_custom, "value", "")
      ).trim();
  }

  const locationOpt = toLocationOption(next.location);
  if (locationOpt) {
    const numericId = Number(locationOpt.value);
    next.job_location_id = isWholeNumber(numericId) ? numericId : null;
    next.location = locationOpt;
  } else if (_.isPlainObject(next.job_location_id)) {
    const nestedId = Number(_.get(next.job_location_id, "value", null));
    next.job_location_id = isWholeNumber(nestedId) ? nestedId : null;
  } else if (next.job_location_id != null && next.job_location_id !== "") {
    const numericId = Number(next.job_location_id);
    next.job_location_id = isWholeNumber(numericId) ? numericId : null;
  } else {
    next.job_location_id = null;
  }

  return next;
};

const getDisplayedJobTitle = (item: ExperienceItem) => {
  const custom = item?.job_title_custom;
  if (_.isPlainObject(custom)) {
    return (
      String(_.get(custom, "label", "") || _.get(custom, "value", "")).trim() ||
      ""
    );
  }
  if (typeof custom === "string" && custom.trim()) return custom.trim();
  return String(_.get(item, "jobTitle.label", "") || "").trim();
};

const getDisplayedLocation = (item: ExperienceItem) => {
  const loc = item?.location;
  if (_.isPlainObject(loc)) {
    return String(
      _.get(loc, "label", "") || _.get(loc, "location", "")
    ).trim();
  }
  if (typeof loc === "string" && loc.trim()) return loc.trim();
  return "";
};

function WorkExperience({ experiences, isPublicPage, openRequest, onOpenHandled }: WorkExperienceProps) {
  const [experienceModal, setExperienceModal] = useState(false);
  const [localData, setLocalData] = useState(experiences);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<WorkExperienceProps>({
    defaultValues: {
      experiences: experiences || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "experiences",
  });

  const candidateViewFlag = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates.candidateViewFlag
  );

  const openExperienceModal = () => {
    if (isPublicPage) return;
    const updatedExperience = (watch("experiences") || []).map((row: any) =>
      normalizeExperienceRowForForm(row)
    );
    reset({ experiences: updatedExperience });
    setExperienceModal(true);
  };

  useEffect(() => {
    if (!openRequest || isPublicPage) return;
    openExperienceModal();
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, isPublicPage]);

  const closeExperienceModal = () => {
    const updatedExperience = (watch("experiences") || []).map((row: any) =>
      normalizeExperienceRowForForm(row)
    );
    reset({ experiences: updatedExperience });
    setExperienceModal(false);
  };

  const onSubmit = (data: WorkExperienceProps) => {
    const payload = {
      experiences: (data?.experiences || []).map(normalizeExperienceRowForApi),
    };

    dispatch(updateCandidateWorkExperienceRequest(payload));
  };

  useEffect(() => {
    const normalized = (experiences || []).map(normalizeExperienceRowForForm);
    reset({ experiences: normalized });
    setLocalData(normalized);
  }, [experiences, reset]);

  useEffect(() => {
    if (candidateViewFlag === "work_experience_success") {
      FlashMessage("Work experience updated successfully");
      const synced = (watch("experiences") || []).map((row: any) =>
        syncPersistedLocationFields(syncPersistedTitleFields(row))
      );
      const updatedData = synced.map((row: any) => normalizeExperienceRowForForm(row));
      setLocalData(updatedData);
      reset({ experiences: updatedData });
      setExperienceModal(false);
      if (!isPublicPage) {
        dispatch(
          mergeSessionUserPartial({
            experiences: synced.map(toSessionExperienceRow),
          })
        );
      }
    }
    if (candidateViewFlag === "work_experience_failed") {
      FlashMessage("Failed to update work experience", "error");
    }

  }, [candidateViewFlag, dispatch, isPublicPage, reset, watch]);

  const _handlePresentCheckBoxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { checked } = e.target;
    if (checked) {
      // Hide degree_to if present is true
      setValue(`experiences.${index}.is_present`, 1);
      setValue(`experiences.${index}.job_ended`, null);
    } else {
      // Optionally set a default or leave it blank
      setValue(`experiences.${index}.is_present`, 0);
    }
  };

  const searchJobTitles = (search: string) =>
    new Promise((resolve) => {
      if (search !== "") {
        clearTimeout(searchDelay);
        searchDelay = setTimeout(async () => {
          const jobTitleDetails = await commonServices.fetchJobTitles(search);
          const data = _.get(jobTitleDetails, "data", []) as {
            id: number;
            title: string;
          }[];
          const locations = data.map((c) => ({
            value: c.id,
            label: c.title,
          }));
          resolve(locations);
        }, 1000);
      } else {
        resolve([]);
      }
    });

  const searchLocations = (
    search: string,
    callback?: (options: { value: number; label: string }[]) => void
  ) => {
    const query = String(search || "").trim();
    clearTimeout(locationSearchDelay);

    if (!query) {
      if (typeof callback === "function") {
        callback([]);
      }
      return;
    }

    locationSearchDelay = setTimeout(function () {
      commonServices.fetchLocations(query).then(function (locationData: any) {
        const locations = _.get(locationData, "data.data", []).map(function (
          c: any
        ) {
          return {
            value: c.id,
            label: c.location,
          };
        });
        if (typeof callback === "function") {
          callback(locations);
        }
      });
    }, 1000);
  };

  return (
    <div className="accordion" id="profile-experience">
      <div className="accordion-head">
        <b>Work Experience</b>
        {!isPublicPage &&
          (!_.isEmpty(localData) ? (
            <div className="edit-icon" onClick={openExperienceModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openExperienceModal}>+Add</span>
          ))}
      </div>
      {!_.isEmpty(localData) && (
        <div className="accordion-body">
          <div
            className={`form-wrapper${isPublicPage
                ? " work-experience-public-page main-public-page profile-timeline-rail"
                : ""
              }`}
          >
            {localData?.map((item, index) => (
              <div
                className={`form-group${isPublicPage ? " profile-timeline-item" : ""}`}
                key={index}
              >
                {isPublicPage && (
                  <span className="profile-timeline-icon" aria-hidden="true">
                    <img src={WorkExperienceIcon} alt="" />
                  </span>
                )}
                {
                  isPublicPage ? (
                    <div>
                      <label className="form-label">
                        {getDisplayedJobTitle(item)}
                      </label>
                      <div className="other-experience-details">
                        <span className="company-name">{_.get(item, "company_name", "")}</span>
                        {getDisplayedLocation(item) ? (
                          <span className="job-location">{getDisplayedLocation(item)}</span>
                        ) : null}
                        <span className="date-range">{_.get(item, "job_started", "")} - {_.get(item, "is_present", 0) === 1 ? "Present" : _.get(item, "job_ended", "")}</span>
                      </div>
                    </div>) : <label className="form-label">
                    {getDisplayedJobTitle(item)}
                  </label>

                }

                <div className="bordered-content wysiwyg-data">
                  <ReadMoreLess text={_.get(item, "description", "")} textLimit={300} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!isPublicPage && (
        <Modal
          className="candidate-profile-modal project-modal services-modal"
          isOpen={experienceModal}
          onClose={closeExperienceModal}
          title={
            !_.isEmpty(localData)
              ? "Edit Work Experience"
              : "Work Experience"
          }
          visible={experienceModal}
          size="large"
        >
          <div className="modal-body-wrapper">
            <form onSubmit={handleSubmit(onSubmit)}>
              {fields && fields.length > 0 ? (
                fields.map((item, index) => (
                  <div className="form-wrapper" key={item.id}>
                    <div className="form-group">
                      <AsyncSelect
                        control={control}
                        validationObj={errors}
                        placeholder="Search Job..."
                        externalLabel={{
                          label: "Job Title",
                          className: "required",
                        }}
                        name={`experiences.${index}.jobTitle`}
                        icon={<img src={JobTitleIcon} alt="" />}
                        onStartSearching={searchJobTitles}
                        noOptionsMessage="No matching job found"
                        rules={{
                          required: {
                            value: true,
                            message: "Please select the Job Title.",
                          },
                          validate: (value: any) => {
                            const opt = toJobTitleOption(value);
                            if (!opt) {
                              return "Please select the Job Title.";
                            }
                            return true;
                          },
                        }}
                      />
                      {/* <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={ProjectTitleIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    name={`experiences[${index}].job_title_custom`}
                    defaultValue={item?.job_title_custom}
                    externalLabel={{ label: "Job Title" }}
                    placeholder="Job title"
                    validationObj={errors}
                    inputRef={register({
                      required: {
                        value: true,
                        message: "Please enter the Job title.",
                      },
                    })}
                  /> */}
                    </div>
                    <div className="form-group">
                      <Input
                        inputIcon={
                          <div className="input-icon">
                            <img src={ExternalLinkIcon} alt="icon" />
                          </div>
                        }
                        className="input-with-icon"
                        defaultValue={item?.company_name}
                        externalLabel={{ label: "Company Name", className: "required" }}
                        placeholder="Company Name"
                        validationObj={errors}
                        {...register(`experiences.${index}.company_name`, {
                          required: {
                            value: true,
                            message: "Please enter the Company Name.",
                          },
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <AsyncSelect
                        control={control}
                        validationObj={errors}
                        placeholder="Select job location"
                        externalLabel={{
                          label: "Job Location",
                          className: "required",
                        }}
                        name={`experiences[${index}].location`}
                        icon={<img src={LocationIcon} alt="" />}
                        onStartSearching={searchLocations}
                        defaultOptions={false}
                        noOptionsMessage="No matching locations found"
                        rules={{
                          required: {
                            value: true,
                            message: "Please select the Job Location.",
                          },
                        }}
                      />
                    </div>
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <div className="form-group">
                          <Input
                            inputIcon={
                              <div className="input-icon">
                                <img src={DateIcon} alt="icon" />
                              </div>
                            }
                            type="date"
                            className="input-with-icon"
                            defaultValue={item.job_started}
                            externalLabel={{ label: "Start Date", className: "required" }}
                            placeholder="Start Date"
                            validationObj={errors}
                            inputProps={{
                              max: new Date().toISOString().split('T')[0]
                            }}
                            {...register(`experiences.${index}.job_started`, {
                              required: {
                                value: true,
                                message: "Please enter the Start Date.",
                              },
                              validate: (value) => {
                                if (!value) return true;
                                const today = new Date().toISOString().split('T')[0];
                                if (value > today) {
                                  return "Start date cannot be in the future";
                                }
                                return true;
                              },
                            })}
                          />
                        </div>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <div className="form-group">
                          {
                            watch(`experiences.${index}.is_present`) ? (
                              <InputLabel className="present-label">Present</InputLabel>
                            ) : (
                              <Input
                                inputIcon={
                                  <div className="input-icon">
                                    <img src={DateIcon} alt="icon" />
                                  </div>
                                }
                                type="date"
                                className="input-with-icon"
                                defaultValue={item?.job_ended}
                                externalLabel={{ label: "End Date" }}
                                placeholder="End Date"
                                validationObj={errors}
                                inputProps={{
                                  min: getValues(`experiences.${index}.job_started`) || item.job_started || "",
                                  max: new Date().toISOString().split('T')[0]
                                }}
                                {...register(`experiences.${index}.job_ended`, {
                                  validate: (value) => {
                                    const isPresent = getValues(`experiences.${index}.is_present`);
                                    if (isPresent === 1 || isPresent === true) return true; // End date not required when Present is selected
                                    if (!value) return "Please enter the End Date.";
                                    const today = new Date().toISOString().split('T')[0];
                                    if (value > today) {
                                      return "End date cannot be in the future";
                                    }
                                    const startDate = getValues(`experiences.${index}.job_started`) || item.job_started;
                                    if (startDate && value < startDate) {
                                      return "End date cannot be less than start date";
                                    }
                                    return true;
                                  },
                                })}
                                disabled={watch(`experiences.${index}.is_present`)}
                              />
                            )}
                        </div>
                      </Grid>
                    </Grid>

                    <div className="from-group">
                      <Checkbox
                        {...register(`experiences.${index}.is_present`, {
                          required: {
                            value: false,
                            message: "Please enter the checkbox.",
                          },
                        })}
                        externalLabel={{ label: "Present" }}
                        defaultChecked={item?.is_present === 1 || item?.is_present === true}
                        onChange={(e: any) =>
                          _handlePresentCheckBoxChange(e, index)
                        }
                      />
                    </div>
                    <div className="form-group ">
                      <RichTextEditor
                        key={item.id}
                        name={`experiences.${index}.description`}
                        defaultValue={item?.description}
                        externalLabel={{
                          label: "Description",
                          className: "required",
                        }}
                        placeholder="Description"
                        validationObj={errors}
                        control={control}
                        rules={{
                          validate: {
                            validLength: (value: any) => {
                              const tmpText = stripHTMLTags(value || "");
                              if (tmpText.length > 1000) {
                                return "Can not exceed more then 1000 characters";
                              }
                              return true;
                            },
                          },
                          required: {
                            value: true,
                            message: "Please enter experience description",
                          },
                        }}
                        showCount
                        maxLimit={1000}
                        counterAlign="right"
                      />
                      <div className="action-wrapper">
                        <div
                          className="icon"
                          onClick={() =>
                            append({
                              id: Date.now(),
                              user_id: 0,
                              job_id: null,
                              job_title_custom: "",
                              is_current_job: 0,
                              company_name: "",
                              description: "",
                              job_started: "",
                              job_ended: null,
                              job_location_id: null,
                              is_present: 0,
                              job_site: null,
                              jobTitle: "",
                              location: null,
                            })
                          }
                        >
                          <img src={PinkPlus} alt="edit" />
                        </div>
                        <div className="icon" onClick={() => remove(index)}>
                          <img src={DeleteIcon} alt="Delete" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <Button
                  type="button"
                  className="add-new-btn"
                  onClick={() =>
                    append({
                      id: Date.now(),
                      user_id: 0,
                      job_id: null,
                      job_title_custom: "",
                      is_current_job: 0,
                      company_name: "",
                      description: "",
                      job_started: "",
                      job_ended: null,
                      job_location_id: null,
                      is_present: 0,
                      job_site: null,
                      jobTitle: "",
                      location: null,
                    })
                  }
                >
                  Add new
                </Button>
              )}
              <div className="btn-wrapper">
                <Button
                  variant="outlined"
                  className="outlined"
                  onClick={closeExperienceModal}
                  color="none"
                >
                  Cancel
                </Button>
                <Button className="save-btn" type="submit">
                  Save
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default WorkExperience;
